import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from '../../services/authService';
import { EssayService } from '../../services/essayService';
import { Essay } from '../../types/Essay';
import PageHeader from '../common/PageHeader';
import './Essays.css';

interface EssaysProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

const Essays: React.FC<EssaysProps> = ({ user, onClose, onToggleSidebar }) => {
  const navigate = useNavigate();
  const [essays, setEssays] = useState<Essay[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newEssayTitle, setNewEssayTitle] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredEssays, setFilteredEssays] = useState<Essay[]>([]);

  useEffect(() => {
    if (user) {
      loadEssays();
    }
  }, [user]);

  useEffect(() => {
    // Filter essays based on search query
    if (searchQuery.trim() === '') {
      setFilteredEssays(essays);
    } else {
      const filtered = essays.filter(essay =>
        essay.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (essay.content && essay.content.toLowerCase().includes(searchQuery.toLowerCase()))
      );
      setFilteredEssays(filtered);
    }
  }, [essays, searchQuery]);

  const loadEssays = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);
      const response = await EssayService.getUserEssays(user.id, {
        limit: 100,
        sort_order: 'DESC'
      });
      setEssays(response.data);
    } catch (err) {
      console.error('Error loading essays:', err);
      setError('Failed to load essays');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEssay = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !newEssayTitle.trim()) return;

    try {
      setError(null);
      const newEssay = await EssayService.createEssayFromTitle(
        user.id,
        newEssayTitle.trim()
      );
      setEssays([newEssay, ...essays]);
      setNewEssayTitle('');
      setShowCreateForm(false);
      navigate(`/essays/${newEssay.essay_id}`);
    } catch (err) {
      console.error('Error creating essay:', err);
      setError('Failed to create essay');
    }
  };

  const handleDeleteEssay = async (essayId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this essay?')) return;

    try {
      setError(null);
      await EssayService.deleteEssay(essayId);
      setEssays(essays.filter(essay => essay.essay_id !== essayId));
    } catch (err) {
      console.error('Error deleting essay:', err);
      setError('Failed to delete essay');
    }
  };

  const handleStatusChange = async (essayId: string, newStatus: 'pending' | 'on-going' | 'completed', e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user) return;

    try {
      setError(null);
      const essay = essays.find(e => e.essay_id === essayId);
      if (!essay) return;

      await EssayService.updateEssay(essayId, {
        title: essay.title,
        content: essay.content,
        status: newStatus
      });
      
      setEssays(essays.map(e => 
        e.essay_id === essayId ? { ...e, status: newStatus } : e
      ));
    } catch (err) {
      console.error('Error updating essay status:', err);
      setError('Failed to update essay status');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return '#666666';
      case 'on-going':
        return '#17a2b8';
      case 'completed':
        return '#28a745';
      default:
        return '#666666';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Pending';
      case 'on-going':
        return 'On-going';
      case 'completed':
        return 'Completed';
      default:
        return status;
    }
  };

  const handleEssayClick = (essayId: string) => {
    navigate(`/essays/${essayId}`);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPreview = (content: string | null, maxLength: number = 100) => {
    if (!content) return '';
    return content.length > maxLength ? content.substring(0, maxLength) + '...' : content;
  };

  if (!user) {
    return (
      <div className="essays-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-center-title">Essays</h1>}
        />
        <div className="essays-auth-message">
          Please sign in to access your essays.
        </div>
      </div>
    );
  }

  return (
    <div className="essays-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<h1 className="page-header-center-title">Essays</h1>}
        rightActions={
          <button 
            className="essays-create-btn"
            onClick={() => setShowCreateForm(!showCreateForm)}
          >
            {showCreateForm ? '✕' : '+'} {showCreateForm ? 'Cancel' : 'New Essay'}
          </button>
        }
      />

      {error && (
        <div className="essays-error">
          {error}
          <button onClick={loadEssays} className="essays-retry-btn">Retry</button>
        </div>
      )}

      {showCreateForm && (
        <div className="essays-create-form">
          <form onSubmit={handleCreateEssay}>
            <div className="create-form-header">
              <span className="create-form-title">Create New Essay</span>
            </div>
            <div className="create-form-content">
              <input
                type="text"
                placeholder="Enter essay title..."
                value={newEssayTitle}
                onChange={(e) => setNewEssayTitle(e.target.value)}
                className="create-essay-input"
                autoFocus
              />
              <div className="create-form-actions">
                <button type="submit" className="create-submit-btn">
                  Create Essay
                </button>
                <button 
                  type="button" 
                  onClick={() => {
                    setShowCreateForm(false);
                    setNewEssayTitle('');
                  }}
                  className="create-cancel-btn"
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      <div className="essays-search">
        <input
          type="text"
          placeholder="Search essays..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="essays-search-input"
        />
      </div>

      <div className="essays-content">
        {loading ? (
          <div className="essays-loading">
            <div className="loading-spinner"></div>
            <p>Loading essays...</p>
          </div>
        ) : filteredEssays.length === 0 ? (
          <div className="essays-empty">
            {searchQuery ? (
              <div>
                <p>No essays found matching "{searchQuery}"</p>
                <button onClick={() => setSearchQuery('')} className="clear-search-btn">
                  Clear search
                </button>
              </div>
            ) : (
              <div>
                <p>You don't have any essays yet.</p>
                <button 
                  onClick={() => setShowCreateForm(true)}
                  className="create-first-essay-btn"
                >
                  Create your first essay
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="essays-list">
            {filteredEssays.map((essay) => (
              <div key={essay.id} className="essay-item" onClick={() => handleEssayClick(essay.essay_id)}>
                <div className="essay-item-header">
                  <h3 className="essay-item-title">{essay.title}</h3>
                  <div className="essay-item-actions">
                    <select
                      className="essay-status-select"
                      value={essay.status}
                      onChange={(e) => handleStatusChange(essay.essay_id, e.target.value as 'pending' | 'on-going' | 'completed', e as any)}
                      onClick={(e) => e.stopPropagation()}
                      title="Change status"
                    >
                      <option value="pending">Pending</option>
                      <option value="on-going">On-going</option>
                      <option value="completed">Completed</option>
                    </select>
                    <button
                      className="essay-item-delete-btn"
                      onClick={(e) => handleDeleteEssay(essay.essay_id, e)}
                      title="Delete essay"
                    >
                      ✕
                    </button>
                  </div>
                </div>
                {essay.content && (
                  <div className="essay-item-preview">
                    {getPreview(essay.content)}
                  </div>
                )}
                <div className="essay-item-meta">
                  <span className="essay-item-id">/{essay.essay_id}</span>
                  <span 
                    className="essay-item-status"
                    style={{ color: getStatusColor(essay.status) }}
                  >
                    {getStatusLabel(essay.status)}
                  </span>
                  <span className="essay-item-date">
                    {formatDate(essay.updated_at)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Essays;

